import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FaChevronDown,
  FaChevronUp,
  FaSuitcase,
  FaChair,
} from "react-icons/fa";
import flightrightIcon from "../../assets/flightright.png";
import FlippableFareCard from "../ui/FlippableFareCard";
import BookingPopup from "./BookingPopup";
import FlightDetailsAccordion from "./FlightDetailsAccordion";
import { computeDurationMinutes, getTimezoneAbbreviation } from "../../utils/dateUtils";
import styles from "../../styles";

interface FareTier {
  label: string;
  price: number;
  currency: string;
  baggage: string;
  cancellation: string;
  addOns: string;
  cabinCode?: string;
  fareBasisCode?: string;
  fareType?: string;
  bookingCodes?: string[];
  scheduleDetails?: Array<{ mealCode?: string; seatsAvailable?: number }>;
  brandFeatures?: Array<{ id: number; commercialName: string }>;
  fullGroup?: any[];
}

interface SegmentDisplayProps {
  origin: string;
  destination: string;
  departureDate: string;
  departureTime?: string;
  arrivalTime?: string;
  duration?: number;
  stops?: number;
  carrier?: string;
}

interface MultiCityFlightCardProps {
  itinerary: any; // SimplifiedItinerary from backend
  selectedFareTierIndex?: number;
  onFareSelect?: (tierIndex: number) => void;
  showFareSelection?: boolean; // Show fare tier radio buttons (for results page)
  expandedByDefault?: boolean; // Auto-expand segments (for booking popup)
  disableExpansion?: boolean; // Lock expansion state
  /** When true (e.g. BookingPopup): do not reset tier on collapse, and header always shows selected tier */
  disableResetOnCollapse?: boolean;
  showBookButton?: boolean; // Show book now button
  /** When true (e.g. PassengerDetail): show ADT fare in header only, not total */
  displayAdtFareOnly?: boolean;
  classOfService?: string;
  selectedClass?: string;
}

const cabinClassOptions = [
  { label: "Economy", code: "Y" },
  { label: "Premium Economy", code: "S" },
  { label: "Business", code: "C" },
  { label: "Premium Business", code: "J" },
  { label: "First class", code: "F" },
  { label: "Premium First", code: "P" },
];

function getCabinLabel(code?: string) {
  return (
    cabinClassOptions.find((c) => c.code === code)?.label || code || "Unknown"
  );
}

const MultiCityFlightCard: React.FC<MultiCityFlightCardProps> = ({
  itinerary,
  selectedFareTierIndex = 0,
  onFareSelect,
  showFareSelection = true,
  expandedByDefault = false,
  disableExpansion = false,
  disableResetOnCollapse = false,
  showBookButton = true,
  displayAdtFareOnly = false,
  classOfService,
  selectedClass,
}) => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(expandedByDefault);
  const [showAllSegments, setShowAllSegments] = useState(expandedByDefault);
  const [selectedTier, setSelectedTier] = useState(selectedFareTierIndex);
  const [showBookingPopup, setShowBookingPopup] = useState(false);

  const segments = itinerary.legs ?? [];
  const totalFare = itinerary.pricingInfo;

  const cabinClassNameToCode: Record<string, string> = {
    economy: "Y", premiumeconomy: "S", business: "C",
    premiumbusiness: "J", first: "F", premiumfirst: "P",
  };
  // Map fareOptions → FareTier (new unified format)
  const brandedFares: FareTier[] = Array.isArray(itinerary.fareOptions)
    ? itinerary.fareOptions.map((fo: any, tierIndex: number) => {
      const rawCabinCode: string | undefined = itinerary.legs?.[0]?.segments?.[0]?.bookingInfo?.cabinCode ?? undefined;
      const rawCabinClass: string | undefined = fo.cabinClass ?? undefined;
      const resolvedCabinCode =
        rawCabinCode ??
        (rawCabinClass
          ? cabinClassNameToCode[rawCabinClass.toLowerCase().replace(/\s+/g, "")]
          : undefined);
      return {
        label: fo.name || `Tier ${tierIndex + 1}`,
        price: fo.passengerPrices?.find((p: any) => p.type === "ADT")?.total ?? fo.price?.total ?? 0,
        currency: fo.price?.currency ?? "AED",
        baggage: fo.baggage?.checked?.description ?? fo.baggage?.cabin?.description ?? "No baggage information",
        cancellation:
          fo.refundPolicy?.conditions ??
          (fo.refundPolicy?.refundable ? "Refundable"
            : fo.refundPolicy?.cancellationFee
              ? `Non-refundable (fee: ${fo.refundPolicy.cancellationFee.amount} ${fo.refundPolicy.cancellationFee.currency})`
              : "Non-refundable"),
        addOns: fo.amenities?.map((a: any) => a.description).join(", ") ?? "",
        cabinCode: resolvedCabinCode,
        fareBasisCode: fo.fareBasis?.code,
        fareType: fo.fareBasis?.type,
        bookingCodes: fo.bookingClasses,
        brandFeatures: fo.amenities?.map((a: any, i: number) => ({ id: i, commercialName: a.description })),
        fullGroup: fo,
      };
    })
    : [];

  // Header: when collapsed show first tier (default); when expanded show selected tier. In popup (disableResetOnCollapse) always show selected tier.
  const selectedTierFare = brandedFares[selectedTier];
  const headerFare = disableResetOnCollapse
    ? selectedTierFare
    : isExpanded
      ? selectedTierFare
      : brandedFares[0];
  const displayedPrice = displayAdtFareOnly
    ? (headerFare?.price ?? 0)
    : (headerFare?.price ?? totalFare?.amount ?? 0);
  const displayedCurrency = displayAdtFareOnly
    ? (headerFare?.currency ?? "AED")
    : (headerFare?.currency ?? totalFare?.currency ?? "AED");
  const displayedClass =
    getCabinLabel(headerFare?.cabinCode) ?? selectedClass ?? "Economy";

  const handleFareSelect = (index: number) => {
    setSelectedTier(index);
    if (onFareSelect) {
      onFareSelect(index);
    }
  };

  const handleBookNow = () => {
    setShowBookingPopup(true);
  };

  const toggleExpansion = () => {
    if (!disableExpansion) {
      if (isExpanded && !disableResetOnCollapse) {
        setSelectedTier(0); // Reset to first tier when collapsing (not in popup)
      }
      setIsExpanded(!isExpanded);
    }
  };

  const toggleSegments = () => {
    setShowAllSegments(!showAllSegments);
  };

  // Format time helper - handles ISO 8601 and HH:mm formats
  const formatSabreTime = (timeStr: string) => {
    if (!timeStr) return "";
    const isoMatch = timeStr.match(/T(\d{2}:\d{2})/);
    if (isoMatch) return isoMatch[1];
    const match = timeStr.match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : timeStr;
  };

  // Format duration helper
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <>
      <div className="bg-[#F5F8FF] rounded-2xl shadow-md overflow-hidden mb-4 transition-all duration-300">
        {/* Header Section */}
        <div
          className={`p-4 sm:p-6 ${!disableExpansion ? "cursor-pointer hover:bg-opacity-90" : ""}`}
          onClick={toggleExpansion}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {/* Multi-city route summary */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm sm:text-base font-semibold text-[#1E1E1E]">
                  Multi-City Journey
                </span>
                <span className="px-2 py-1 bg-primary bg-opacity-10 text-primary text-xs rounded-full font-medium">
                  {segments.length} Segments
                </span>
              </div>

              {/* Route overview - Show each segment separately */}
              <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-[#1E1E1E]">
                {segments.map((leg: any, idx: number) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span className="text-gray-500 mx-1">•</span>}
                    <div className="flex items-center gap-1">
                      <span className="font-medium">
                        {leg.departure?.airport?.code ?? leg.departureAirport}
                      </span>
                      <span className="text-gray-500">→</span>
                      <span className="font-medium">
                        {leg.arrival?.airport?.code ?? leg.arrivalAirport}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Price - Dynamic based on selected tier, Match FlightCard.tsx styling exactly */}
            <div className="text-right ml-4 min-w-[80px] sm:min-w-[100px]">
              <p className="text-sm sm:text-md font-sora font-bold">
                {displayedClass}
              </p>
              <p className="text-base sm:text-lg font-semibold">
                {displayedCurrency} {displayedPrice}
              </p>
            </div>

            {/* Expand icon */}
            {!disableExpansion && (
              <div className="ml-4">
                {isExpanded ? (
                  <FaChevronUp className="text-primary" />
                ) : (
                  <FaChevronDown className="text-primary" />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-300">
            {/* Segments Section */}
            <div className="p-4 sm:p-6 bg-white bg-opacity-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[#1E1E1E] text-sm sm:text-base">
                  Flight Segments
                </h3>
                {segments.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSegments();
                    }}
                    className="text-xs sm:text-sm text-primary hover:text-opacity-80 font-medium"
                  >
                    {showAllSegments ? "Hide Details" : "Show All Segments"}
                  </button>
                )}
              </div>

              {/* Display segments */}
              {segments.map((leg: any, index: number) => {
                if (!showAllSegments && index > 0) return null;

                // New format: leg has segments[] for individual flight segments
                const firstSchedule = leg.segments?.[0];
                const lastSchedule = leg.segments?.[leg.segments?.length - 1];

                // Tier-specific details for this leg
                const segmentFares = (selectedTierFare?.fullGroup as any)?.legPricing?.[index];
                const seatsAvailable = firstSchedule?.bookingInfo?.seatsAvailable;
                const mealCode = firstSchedule?.bookingInfo?.meal?.code;

                function formatMealCode(code?: string) {
                  if (!code) return null;
                  const map: Record<string, string> = {
                    M: "Meal", S: "Snack", RF: "Refreshment", F: "Full meal",
                    D: "Dinner", B: "Breakfast", L: "Lunch",
                  };
                  return map[code] || code;
                }

                const airlineCode =
                  firstSchedule?.operatingCarrier?.code ||
                  firstSchedule?.marketingCarrier?.code ||
                  "";
                const airlineLogo = `/assets/airlines/${airlineCode}.png`;
                const airlineName = airlineCode;
                const flightNumber =
                  `${firstSchedule?.marketingCarrier?.code || ""} ${firstSchedule?.marketingCarrier?.flightNumber || ""}`.trim();

                // Safely extract datetime
                const departureTime = leg.departure?.dateTime
                  ? formatSabreTime(leg.departure.dateTime)
                  : "--:--";

                const arrivalTime = leg.arrival?.dateTime
                  ? formatSabreTime(leg.arrival.dateTime)
                  : "--:--";

                // Next day arrival
                const hasDateAdjustment = (leg.arrival?.dateAdjustment ?? 0) > 0;

                // Get stop summary (same as FlightCard)
                const getStopSummary = (schedules: any[]) => {
                  const stops = schedules.length - 1;
                  if (stops <= 0) return "Non-stop";
                  const stopCodes = schedules
                    .slice(0, -1)
                    .map((seg: any) => seg.arrival?.airport)
                    .filter(Boolean);
                  return `${stops} stop${stops > 1 ? "s" : ""} (${stopCodes.join(", ")})`;
                };

                return (
                  <div
                    key={index}
                    className="bg-[#F5F8FF] rounded-xl p-4 mb-2 shadow-sm border border-gray-200"
                  >
                    {/* Airline Logo and Info Section - Match FlightCard styling exactly */}
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={airlineLogo}
                        alt={airlineName}
                        className="w-10 h-10 sm:w-16 sm:h-16 object-contain rounded-lg"
                        onError={(e) => {
                          const el = e.target as HTMLImageElement;
                          const fallback = "/assets/airlines/default.png";
                          if (
                            el.src.endsWith(fallback) ||
                            el.src.endsWith("default.png")
                          )
                            return;
                          el.src = fallback;
                        }}
                      />
                      <div className="flex flex-col">
                        <span className="text-base sm:text-lg font-sora font-semibold">
                          {flightNumber || `Segment ${index + 1}`}
                        </span>
                        <span className="text-xs sm:text-sm text-[#1E1E1E]">
                          {airlineName}
                        </span>
                        {(seatsAvailable !== undefined || mealCode) && (
                          <div className="flex flex-col gap-0.5 text-2xs sm:text-xs text-gray-500 mt-1">
                            {seatsAvailable !== undefined && (
                              <span className="flex items-center gap-1">
                                <FaChair className="inline-block" />
                                {seatsAvailable} available
                              </span>
                            )}
                            {mealCode && (
                              <span className="text-gray-400">
                                {formatMealCode(mealCode)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Baggage */}
                    <div className="flex items-center justify-center mb-2">
                      <span className="flex items-center gap-1 text-2xs sm:text-xs font-semibold text-primary">
                        <FaSuitcase className="inline-block mr-1" />
                        <span className="font-bold">
                          {selectedTierFare?.baggage ??
                            firstSchedule?.bookingInfo?.baggage ??
                            "No baggage information"}
                        </span>
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      {/* Origin */}
                      <div className="text-center">
                        <div className="text-2xs sm:text-xs font-medium">
                          {departureTime}
                          {(() => {
                            const tz = getTimezoneAbbreviation(leg.departure?.dateTime);
                            return tz ? (
                              <span className="ml-1 text-[9px] font-normal text-gray-500">{tz}</span>
                            ) : null;
                          })()}
                        </div>
                        <div className="text-xs sm:text-base font-bold">
                          {leg.departure?.airport?.code ?? leg.departureAirport ?? "---"}
                        </div>
                      </div>

                      {/* Flight direction icon */}
                      <div className="flex-1 flex items-center justify-center mx-2">
                        <img
                          src={flightrightIcon}
                          alt="flight direction"
                          className="w-5 h-5 sm:w-7 sm:h-7 mx-1"
                        />
                      </div>

                      {/* Destination */}
                      <div className="text-center">
                        <div className="text-2xs sm:text-xs font-medium">
                          {arrivalTime}
                          {hasDateAdjustment && (
                            <span className="ml-1 text-2xs sm:text-cardsmall text-primary font-sora font-semibold">
                              (next day)
                            </span>
                          )}
                          {(() => {
                            const tz = getTimezoneAbbreviation(leg.arrival?.dateTime);
                            return tz ? (
                              <span className="ml-1 text-[9px] font-normal text-gray-500">{tz}</span>
                            ) : null;
                          })()}
                        </div>
                        <div className="text-xs sm:text-base font-bold">
                          {leg.arrival?.airport?.code ?? leg.arrivalAirport ?? "---"}
                        </div>
                      </div>
                    </div>

                    {/* Stops & duration */}
                    <p className="text-2xs sm:text-xs text-gray-500 text-center mt-1">
                      {(() => {
                        const legSegs = leg.segments ?? [];
                        const stops = legSegs.length - 1;
                        if (stops <= 0) return "Non-stop";
                        const codes = legSegs.slice(0, -1)
                          .map((s: any) => s.arrival?.airport?.code ?? s.arrival?.airport)
                          .filter(Boolean);
                        return `${stops} stop${stops > 1 ? "s" : ""} (${codes.join(", ")})`;
                      })()} - Duration:{" "}
                      {formatDuration(
                        leg.duration ||
                          computeDurationMinutes(
                            leg.departure?.dateTime,
                            leg.arrival?.dateTime,
                            leg.arrival?.dateAdjustment ?? 0,
                          ),
                      )}
                    </p>

                    {/* Operating carrier info */}
                    {firstSchedule?.operatingCarrier?.code &&
                      firstSchedule?.operatingCarrier?.code !==
                      firstSchedule?.marketingCarrier?.code && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span className="text-gray-500">
                              Operated by {firstSchedule.operatingCarrier.code}
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                );
              })}
            </div>

            {/* Fare Selection - Match FlightCard layout logic */}
            {showFareSelection && brandedFares.length > 0 && (
              <div className="mb-4">
                <div className="font-bold text-primary mb-2 mx-2 sm:mx-4 text-xs sm:text-subheading">
                  Select required Fare class
                </div>
                <div
                  className={`p-2 sm:p-4 gap-2 sm:gap-4 ${brandedFares.length <= 3
                    ? `${styles.flexCenter} flex-col sm:flex-row`
                    : brandedFares.length === 4
                      ? "grid grid-cols-1 sm:grid-cols-2 items-stretch"
                      : "grid grid-cols-1 sm:grid-cols-3 items-stretch"
                    }`}
                >
                  {brandedFares.map((tier, index) => (
                    <div
                      key={`fare-${index}`}
                      className={`${selectedTier === index
                        ? "border-primary border-2"
                        : "border-gray-200 border"
                        } rounded-xl overflow-hidden ${brandedFares.length <= 3 ? "flex-1 max-w-xs w-full" : ""
                        }`}
                    >
                      <FlippableFareCard
                        fare={tier}
                        brandFeatures={tier.brandFeatures ?? []}
                        brandName={tier.label}
                        selected={selectedTier === index}
                        onClick={() => handleFareSelect(index)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Show message if no fares */}
            {showFareSelection && brandedFares.length === 0 && (
              <div className="p-4 border-t border-gray-200 bg-amber-50">
                <p className="text-sm text-amber-800 text-center">
                  Fare options are being loaded...
                </p>
              </div>
            )}

            {/* Book Now Button - Same style as FlightCard.tsx (no border above), with padding */}
            {showBookButton && (
              <div className="text-center mt-6 mb-2 px-4 sm:px-8 py-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBookNow();
                  }}
                  className="bg-white text-[#0B2F9F] min-w-[140px] sm:min-w-[160px] font-bold px-8 py-3 rounded-3xl shadow-md hover:bg-gray-100 transition"
                >
                  Book Now
                </button>
              </div>
            )}
          </div>
        )}

        {/* View Flight Details accordion */}
        {!disableExpansion && (
          <FlightDetailsAccordion
            flightData={itinerary}
            tier={selectedTierFare as any}
            baseDepartureDate={
              itinerary?.legs?.[0]?.departure?.dateTime ??
              location.state?.segments?.[0]?.departureDate
            }
            fallbackBaggage={selectedTierFare?.baggage}
            fallbackCurrency={
              selectedTierFare?.currency ?? totalFare?.currency ?? "AED"
            }
            fallbackBaseFare={totalFare?.baseFare}
            fallbackTaxes={totalFare?.taxes}
            fallbackTotal={totalFare?.amount ?? totalFare?.total}
          />
        )}
      </div>

      {/* Booking Popup */}
      {showBookingPopup && (
        <BookingPopup
          onClose={() => setShowBookingPopup(false)}
          flightData={itinerary}
          passengerCounts={location.state?.passengerCounts ?? { ADT: 1 }}
          departureDate={location.state?.segments?.[0]?.departureDate}
          classOfService={classOfService || "Y"}
          selectedClass={selectedClass || "Economy"}
          selectedFareTierIndex={selectedTier}
          tripType="multicity"
          multicitySegmentDates={
            location.state?.segments
              ?.map((s: { departureDate?: string }) => s.departureDate)
              ?.filter(Boolean) as string[] | undefined
          }
        />
      )}
    </>
  );
};

export default MultiCityFlightCard;
